import { createBrowserRouter, Navigate } from '../compat/router-shim';
import { Layout } from '../components/templates/Layout';
import { routePaths } from './basePaths';
import { PageNotFound } from '../pages/404';
import { NewInspection } from '../pages/NewInspection';
import { homeRoute } from './home.routes';
import { inspectionRoutes } from './inspection.routes';
import { LoginScreen } from '../pages/LoginScreen';
import { NewBlankBucket } from '../pages/inspections/bucket/NewBlankBucket';
import { protectedRouteLoader } from './protectedRouteLoader';

// Create router lazily to ensure MSAL is initialized first
let routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

const getRouter = () => {
  if (routerInstance) return routerInstance;

  routerInstance = createBrowserRouter([
    {
      path: '/login',
      element: <LoginScreen />,
    },
    {
      path: '/',
      element: <Layout />,
      errorElement: <PageNotFound />,
      HydrateFallback: () => (
        <div className="flex h-screen items-center justify-center">
          <div className="text-center">
            <div className="mx-auto size-12 animate-spin rounded-full border-b-2 border-brand"></div>
            <p className="mt-4 text-inkMuted">Loading...</p>
          </div>
        </div>
      ),
      loader: protectedRouteLoader,
      children: [
        homeRoute,
        {
          path: routePaths.NewInspection,
          element: <NewInspection />,
        },
        {
          // The history screen folded into the front page (its tabs, search and
          // paged list live on Home now). Redirect keeps stale PWA links alive.
          path: '/history',
          element: <Navigate to="/" replace />,
        },
        {
          path: routePaths.Summary,
          lazy: async () => {
            const { SummaryScreen } = await import('../pages/SummaryScreen');
            return {
              element: <SummaryScreen />,
            };
          },
        },
        {
          path: routePaths.TableOfBucket,
          lazy: async () => {
            const { TableOfBucketScreen } =
              await import('../pages/TableOfBucketScreen');
            return {
              element: <TableOfBucketScreen />,
            };
          },
        },
        {
          path: routePaths.HelpScreen,
          lazy: async () => {
            const { HelpScreen } = await import('../pages/help/HelpScreen');
            return {
              element: <HelpScreen />,
            };
          },
          children: [
            {
              path: routePaths.HelpScreen + '/:categoryName',
              lazy: async () => {
                const { HelpScreen } = await import('../pages/help/HelpScreen');
                return {
                  element: <HelpScreen />,
                };
              },
            },
            {
              path: routePaths.HelpScreen + '/:categoryName/:questionId',
              lazy: async () => {
                const { HelpScreen } = await import('../pages/help/HelpScreen');
                return {
                  element: <HelpScreen />,
                };
              },
            },
          ],
        },
        ...inspectionRoutes,
        {
          path: routePaths.NewBucket,
          element: <NewBlankBucket />,
        },
        {
          path: routePaths.SupportScreen,
          lazy: async () => {
            const { SupportScreen } = await import('../pages/SupportScreen');
            return {
              element: <SupportScreen />,
            };
          },
        },
        // you can continue here without worry about layout
      ],
    },
  ]);

  return routerInstance;
};

// Export router getter for use in App
export const router = getRouter;
